import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BellIcon, XMarkIcon, CheckIcon, ClockIcon } from '@heroicons/react/24/outline';
import { BellAlertIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { api, notificationsApi } from '../services/api';

interface AlertData {
  id: string;
  alert_type: string;
  severity: string;
  title: string;
  message: string;
  related_po_id?: string;
  po_number?: string;
  status: string;
  created_at: string;
}

interface NotificationCenterProps {
  className?: string;
}

export default function NotificationCenter({ className = '' }: NotificationCenterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch alerts from the alerts API
  const { data: alerts = [], isLoading } = useQuery<AlertData[]>({
    queryKey: ['notifications-alerts'],
    queryFn: async () => {
      try {
        const response = await api.get('/alerts', {
          params: { status: 'Active', limit: 50 }
        });
        return response.data.data || [];
      } catch {
        return [];
      }
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Acknowledge alert mutation (mark as read)
  const acknowledgeAlert = useMutation({
    mutationFn: (id: string) => api.post(`/alerts/${id}/acknowledge`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-alerts'] });
    },
  });

  // Acknowledge all alerts
  const acknowledgeAllAlerts = useMutation({
    mutationFn: () => api.post('/alerts/acknowledge-all'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-alerts'] });
    },
  });

  // Dismiss alert mutation
  const dismissAlert = useMutation({
    mutationFn: (id: string) => api.post(`/alerts/${id}/dismiss`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-alerts'] });
    },
  });

  // Snooze alert mutation
  const snoozeAlert = useMutation({
    mutationFn: ({ id, duration }: { id: string; duration: string }) =>
      notificationsApi.snoozeAlert(id, { duration }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-alerts'] });
    },
  });

  // Snooze dropdown state
  const [snoozeOpenId, setSnoozeOpenId] = useState<string | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Listen for push notification clicks
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.type === 'NOTIFICATION_CLICK') {
          navigate(event.data.url);
        }
      });
    }
  }, [navigate]);

  const unreadCount = alerts.length;

  const getIcon = (alertType: string): string => {
    switch (alertType) {
      case 'payment_overdue':
      case 'payment_due_soon':
        return '💰';
      case 'moh_permit_expiring':
      case 'mot_permit_expiring':
        return '📋';
      case 'contract_expiring':
        return '📄';
      case 'permit_coverage_gap':
        return '⚠️';
      case 'shipment_eta_today':
        return '🚢';
      default:
        return '🔔';
    }
  };

  const getSeverityStyles = (severity: string): string => {
    switch (severity) {
      case 'Critical':
        return 'bg-red-100 text-red-700';
      case 'Warning':
        return 'bg-amber-100 text-amber-700';
      case 'Info':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getActionUrl = (alert: AlertData): string | null => {
    if (alert.related_po_id) {
      return `/pos/${alert.related_po_id}`;
    }
    if (alert.alert_type.includes('payment')) {
      return '/payments';
    }
    if (alert.alert_type.includes('permit')) {
      return '/permits';
    }
    if (alert.alert_type.includes('contract')) {
      return '/permits?tab=contracts';
    }
    return null;
  };

  const handleAlertClick = (alert: AlertData) => {
    const actionUrl = getActionUrl(alert);
    if (actionUrl) {
      navigate(actionUrl);
      setIsOpen(false);
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        aria-label="Notifications"
      >
        {unreadCount > 0 ? (
          <BellAlertIcon className="w-6 h-6 text-primary" />
        ) : (
          <BellIcon className="w-6 h-6 text-gray-500" />
        )}

        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[20px] h-5 px-1 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 md:w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h3 className="font-semibold text-gray-900">Alerts</h3>
            {unreadCount > 0 && (
              <button
                onClick={() => acknowledgeAllAlerts.mutate()}
                className="text-sm text-primary hover:text-primary/80"
              >
                Acknowledge all
              </button>
            )}
          </div>

          {/* Alerts List */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            ) : alerts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                <BellIcon className="w-12 h-12 mb-2 text-gray-300" />
                <p>No active alerts</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {alerts.slice(0, 20).map((alert) => (
                  <div
                    key={alert.id}
                    className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors bg-blue-50/50"
                    onClick={() => handleAlertClick(alert)}
                  >
                    <div className="flex gap-3">
                      {/* Icon */}
                      <div className="flex-shrink-0 text-xl">
                        {getIcon(alert.alert_type)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-medium text-gray-900">
                            {alert.title}
                          </p>
                          <span className={`flex-shrink-0 px-1.5 py-0.5 text-xs rounded ${getSeverityStyles(alert.severity)}`}>
                            {alert.severity}
                          </span>
                        </div>

                        <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">
                          {alert.message}
                        </p>

                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-gray-400">
                            {formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}
                          </span>

                          {alert.po_number && (
                            <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                              {alert.po_number}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex-shrink-0 flex flex-col gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            acknowledgeAlert.mutate(alert.id);
                          }}
                          className="p-1 text-gray-400 hover:text-green-500"
                          title="Acknowledge"
                        >
                          <CheckIcon className="w-4 h-4" />
                        </button>
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSnoozeOpenId(snoozeOpenId === alert.id ? null : alert.id);
                            }}
                            className="p-1 text-gray-400 hover:text-amber-500"
                            title="Snooze"
                          >
                            <ClockIcon className="w-4 h-4" />
                          </button>
                          {snoozeOpenId === alert.id && (
                            <div className="absolute right-0 mt-1 w-32 bg-white border rounded-lg shadow-lg z-50 py-1">
                              {[
                                { label: '1 hour', value: '1h' },
                                { label: '4 hours', value: '4h' },
                                { label: 'Tomorrow', value: 'tomorrow' },
                              ].map(opt => (
                                <button
                                  key={opt.value}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    snoozeAlert.mutate({ id: alert.id, duration: opt.value });
                                    setSnoozeOpenId(null);
                                  }}
                                  className="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100"
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            dismissAlert.mutate(alert.id);
                          }}
                          className="p-1 text-gray-400 hover:text-red-500"
                          title="Dismiss"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {alerts.length > 0 && (
            <div className="px-4 py-2 border-t bg-gray-50">
              <button
                onClick={() => {
                  navigate('/alerts');
                  setIsOpen(false);
                }}
                className="w-full text-center text-sm text-primary hover:text-primary/80"
              >
                View all alerts
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
